// 智能体接口封装（SSE 流式）
import authStore from '../store/authStore'
// 本地开发走 devServer 代理（见 vue.config.js proxy），避免跨域；
// 生产环境需在 nginx 等反向代理中配置同样的 /api 转发规则
// const BASE_URL = '/api/chat'
const BASE_URL = 'https://www.lxfly.top/agent-api/chat'

// 智能体对话接口地址，如与实际部署不一致在此调整
export const AI_CHAT_URL = BASE_URL

/**
 * SSE 流式对话
 * @param {Object} payload 请求体结构: { message: string, history: [{role: string, content: string}] }
 * @param {Object} handlers { onEvent(evt), onDone(), onError(err) }
 *   evt: {type:'token',content} / {type:'tool_start',name} /
 *        {type:'tool_end',name,output,coord?,boundary?,chart?} / {type:'error',message}
 * @returns {Function} abort 取消函数
 */
export function chatStream(payload, handlers = {}) {
  const controller = new AbortController()
  const token = authStore.state.token
  const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream' }
  if (token) headers['Authorization'] = token

  fetch(AI_CHAT_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: controller.signal
  })
    .then(async (response) => {
      if (!response.ok) throw new Error('HTTP ' + response.status)
      const contentType = response.headers.get('content-type') || ''

      // 非 SSE：整体读取后一次性回调（兼容普通 JSON/文本返回）
      if (!contentType.includes('text/event-stream')) {
        const text = await response.text()
        let out = text
        try {
          out = extractText(JSON.parse(text)) || text
        } catch (e) { /* 非 JSON，按纯文本处理 */ }
        if (out) handlers.onEvent && handlers.onEvent({ type: 'token', content: out })
        handlers.onDone && handlers.onDone()
        return
      }

      // SSE 流式解析
      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')
      let buffer = ''
      let finished = false
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let idx
        while ((idx = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, idx).trim()
          buffer = buffer.slice(idx + 1)
          if (!line.startsWith('data:')) continue
          const data = line.slice(5).trim()
          if (!data) continue
          if (data === '[DONE]') {
            finished = true
            handlers.onDone && handlers.onDone()
            return
          }
          const evt = parseSseEvent(data)
          if (evt) handlers.onEvent && handlers.onEvent(evt)
        }
      }
      if (!finished) handlers.onDone && handlers.onDone()
    })
    .catch((err) => {
      if (err && err.name === 'AbortError') return
      handlers.onError && handlers.onError(err)
    })

  return () => controller.abort()
}

// 解析单条 SSE data 为事件对象，兼容标准事件协议与旧版纯文本格式
function parseSseEvent(data) {
  let json = null
  try { json = JSON.parse(data) } catch (e) { /* 纯文本 */ }
  if (json && typeof json === 'object') {
    // 标准事件协议：{type: token|tool_start|tool_end|error, ...}
    if (typeof json.type === 'string') return json
    // 兼容旧格式：无 type 字段时提取文本作为 token 事件
    const text = extractText(json)
    return text ? { type: 'token', content: text } : null
  }
  return data ? { type: 'token', content: data } : null
}

// 从常见返回结构中提取文本内容
function extractText(json) {
  if (typeof json === 'string') return json
  if (!json || typeof json !== 'object') return ''
  for (const key of ['content', 'text', 'message', 'answer', 'result']) {
    if (typeof json[key] === 'string') return json[key]
  }
  if (json.data) return extractText(json.data)
  if (json.choices && json.choices[0]) {
    const c = json.choices[0]
    if (c.delta && typeof c.delta.content === 'string') return c.delta.content
    if (c.message && typeof c.message.content === 'string') return c.message.content
    if (typeof c.text === 'string') return c.text
  }
  return ''
}
